import { MdChevronLeft, MdChevronRight, MdOutlineInfo } from 'react-icons/md';

import { Loading, Table, TableFooter } from 'components';
import { NoTransactionsIcon } from 'static/icons';
import { Pagination, PixTransaction } from 'types';

import { TransactionRows } from '../transaction-rows';

interface TransactionsListProps {
  transactions: PixTransaction[];
  pagination: Pagination | undefined;
  isLoading: boolean;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  onPageChange: (page: number) => void;
}

interface PaginationComponentProps {
  /** Página atual (0-based) */
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPageChange: (page: number) => void;
}

/**
 * PaginationComponent
 *
 * Componente de paginação criado para cenários onde o backend **não consegue
 * informar o total de páginas** (`totalPages`), devido a limitações na query
 * (ex: relatórios que consolidam dados de múltiplas tabelas).
 *
 * Neste relatório específico:
 * - O backend **sempre retorna `totalPages = 0`**
 * - A paginação é controlada manualmente via código
 * - O front determina se existem páginas anteriores ou próximas
 *
 * Estratégia utilizada:
 * - `hasPrevious`: indica se existe página anterior
 * - `hasNext`: indica se existe próxima página
 * - `page`: página atual (baseada em índice, 0-based)
 * - `count`: quantidade solicitada ao backend
 * - `totalCount`: quantidade de registros retornados pelo backend
 *
 * ⚠️ Observação:
 * Este componente **não exibe total de páginas** e **não depende de `totalPages`**,
 * sendo adequado apenas para fluxos onde a navegação ocorre via
 * "página anterior / página atual / próxima página".
 *
 * Caso o backend passe a fornecer `totalPages`, este componente deverá ser
 * revisado ou substituído por uma paginação tradicional.
 */
function PaginationComponent({
  page,
  hasPrevious,
  hasNext,
  onPageChange,
}: PaginationComponentProps) {
  const currentPageLabel = page + 1;

  const baseButtonClassNames =
    'flex items-center justify-center size-8 rounded-lg border border-solid';

  const pageButtonClassNames = `${baseButtonClassNames} border-neutral-300 cursor-pointer`;
  const activePageButtonClassNames = `${baseButtonClassNames} bg-neutral-700 text-neutral-100`;
  const arrowButtonClassNames =
    'py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed';

  const goToPreviousPage = () => {
    if (!hasPrevious) return;
    onPageChange(page - 1);
  };

  const goToNextPage = () => {
    if (!hasNext) return;
    onPageChange(page + 1);
  };

  return (
    <div className="flex items-center justify-center gap-4">
      <button
        aria-label="Página anterior"
        onClick={goToPreviousPage}
        disabled={!hasPrevious}
        className={arrowButtonClassNames}
      >
        <MdChevronLeft />
      </button>

      {hasPrevious && (
        <button onClick={goToPreviousPage} className={pageButtonClassNames}>
          {currentPageLabel - 1}
        </button>
      )}

      <button aria-current="page" className={activePageButtonClassNames}>
        {currentPageLabel}
      </button>

      {hasNext && (
        <button onClick={goToNextPage} className={pageButtonClassNames}>
          {currentPageLabel + 1}
        </button>
      )}

      <button
        aria-label="Próxima página"
        onClick={goToNextPage}
        disabled={!hasNext}
        className={arrowButtonClassNames}
      >
        <MdChevronRight />
      </button>
    </div>
  );
}

export function PixTransactionsTable({
  transactions,
  pagination,
  isLoading,
  pageSize,
  onPageSizeChange,
  onPageChange,
}: TransactionsListProps) {
  if (isLoading) return <Loading />;

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-12">
        <NoTransactionsIcon />
        <p className="text-center text-sm font-bold text-gray-600">
          Não há transações de Pix no momento, aguarde ou contate o suporte
        </p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <Table
        headers={[
          { name: 'Criação' },
          { name: 'Pedido/Remessa' },
          { name: 'ID' },
          { name: 'Tipo' },
          { name: 'Origem' },
          { name: 'Destino' },
          { name: 'Valor Bruto' },
          {
            name: 'Taxa',
            description:
              'Em estornos, taxas positivas indicam a restituição da tarifa.',
            icon: <MdOutlineInfo size={16} />,
          },
          { name: 'Líquido' },
          { name: 'Status' },
          { name: 'Atualização' },
          { name: 'Informação' },
        ]}
        noResults={!Boolean(transactions?.length)}
      >
        <TransactionRows transactions={transactions} />
      </Table>

      <TableFooter
        pagination={pagination}
        changeCurrentPage={onPageChange}
        pageSize={pageSize}
        setPageSize={onPageSizeChange}
        isLoading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationComponent={
          pagination && (
            <PaginationComponent
              hasPrevious={pagination.hasPrevious}
              hasNext={pagination.hasNext}
              page={pagination.page}
              onPageChange={onPageChange}
            />
          )
        }
      />
    </div>
  );
}
